'use client';

import { useResolvedRoutes } from '@/lib/use-routes';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import type {
  EntityFormConfig,
  EntityFormValues,
} from '@/components/app/entity-editor';
import type { EntityEditorState } from '@/features/entity-editors/registry';
import { useGetStripeConnection } from '@/features/integrations/api';
import { useListLocations } from '@/features/organization-locations';
import { useListServices } from '@/features/organization-services';
import { ServiceDepositField } from '@/features/services-dashboard/service-form';

import type { PackageWithItems } from '../api';
import {
  PackageBranchesField,
  PackageItemsField,
  PackagePriceField,
  PackageSellOnlineField,
  PackageSummaryAside,
  PackageValidityField,
} from './package-form-fields';
import { packageLabels as L, type PackageFormValues } from './package.form';
import { usePackageForm } from './use-package-form';

/**
 * The package editor — CONFIG AND STATE ONLY, no layout.
 *
 * One section, three jobs in the order a clinic thinks about a bundle: what it
 * is, what it contains and costs, and what is paid up front. The payment block
 * is the service editor's own `ServiceDepositField`, so a package is configured
 * exactly as a service is. The aside sums the bundle against booking the same
 * services one by one — the number a clinic prices a package against.
 */
export function usePackageEditor({
  pkg,
}: {
  /** Null → create mode. */
  pkg: PackageWithItems | null;
}): EntityEditorState {
  const navigate = useNavigate();
  const routes = useResolvedRoutes();
  const { services } = useListServices();
  const { locations } = useListLocations();
  // A deposit can only be switched on when Stripe Connect can actually charge.
  const { chargesEnabled, hasIntegration } = useGetStripeConnection();

  const form = usePackageForm({
    pkg,
    onSuccess: () => navigate({ to: routes.catalogPackages }),
  });
  const { values, patch } = form;

  const setValue = useCallback(
    (name: string, value: unknown) =>
      patch({ [name]: value } as Partial<PackageFormValues>),
    [patch]
  );

  const config: EntityFormConfig = useMemo(
    () => ({
      title: (isEdit) => (isEdit ? 'Edit package' : 'Add package'),
      aside: (
        <PackageSummaryAside
          format={form.format}
          services={services}
          values={values}
        />
      ),
      sections: [
        {
          id: 'details',
          label: 'Details',
          blocks: [
            {
              title: 'Package Details',
              rows: [
                [
                  {
                    kind: 'text',
                    name: 'name',
                    label: L.name,
                    placeholder: 'e.g. Glow course',
                  },
                ],
                [
                  {
                    kind: 'textarea',
                    name: 'description',
                    label: L.description,
                    placeholder: 'What is included, and who it is for',
                  },
                ],
              ],
            },
            {
              title: 'Services and Price',
              rows: [
                [
                  {
                    kind: 'custom',
                    name: 'items',
                    render: () => (
                      <PackageItemsField
                        currencySymbol={form.currencySymbol}
                        items={values.items}
                        onAdd={form.addItem}
                        onRemove={form.removeItem}
                        onServiceChange={(index, serviceId) =>
                          form.setItem(index, { serviceId })
                        }
                        onQuantityChange={(index, quantity) =>
                          form.setItem(index, { quantity })
                        }
                        services={services}
                      />
                    ),
                  },
                ],
                [
                  {
                    kind: 'custom',
                    name: 'priceRaw',
                    render: () => (
                      <PackagePriceField
                        currencySymbol={form.currencySymbol}
                        onChange={(priceRaw) => patch({ priceRaw })}
                        value={values.priceRaw}
                      />
                    ),
                  },
                  {
                    kind: 'custom',
                    name: 'validityDays',
                    render: () => (
                      <PackageValidityField
                        onChange={(validityDays) => patch({ validityDays })}
                        value={values.validityDays}
                      />
                    ),
                  },
                ],
              ],
            },
            {
              title: 'Online booking',
              rows: [
                [
                  {
                    kind: 'custom',
                    name: 'sellOnline',
                    render: () => (
                      <PackageSellOnlineField
                        checked={values.sellOnline}
                        onChange={(sellOnline) => patch({ sellOnline })}
                      />
                    ),
                  },
                ],
                [
                  {
                    kind: 'custom',
                    name: 'locationIds',
                    render: () =>
                      values.sellOnline ? (
                        <PackageBranchesField
                          locations={locations}
                          onChange={(locationIds) => patch({ locationIds })}
                          value={values.locationIds}
                        />
                      ) : null,
                  },
                ],
              ],
            },
            {
              title: 'Deposits',
              rows: [
                [
                  {
                    kind: 'custom',
                    name: 'requiresDeposit',
                    render: () => (
                      <ServiceDepositField
                        currencySymbol={form.currencySymbol}
                        depositAmount={values.depositAmount}
                        depositBasis={values.depositBasis}
                        depositPercent={values.depositPercent}
                        depositsAvailable={chargesEnabled}
                        noun="package"
                        onDepositAmountChange={(depositAmount) =>
                          patch({ depositAmount })
                        }
                        onDepositBasisChange={(depositBasis) =>
                          patch({ depositBasis })
                        }
                        onDepositPercentChange={(depositPercent) =>
                          patch({ depositPercent })
                        }
                        onRequiresDepositChange={form.setRequiresDeposit}
                        paymentPolicy={values.paymentPolicy}
                        requiresDeposit={values.requiresDeposit}
                        stripeConnected={hasIntegration}
                      />
                    ),
                  },
                ],
              ],
            },
          ],
        },
      ],
    }),
    [form, values, patch, services, locations, chargesEnabled, hasIntegration]
  );

  return {
    config,
    values: values as unknown as EntityFormValues,
    setValue,
    isSaving: form.isSaving,
    onSave: () => void form.submit(),
    onCancel: () => navigate({ to: routes.catalogPackages }),
  };
}
